import styled, { css } from 'styled-components';

import {
  Anchor,
  Flex,
  IconCircle,
  IconZillowLogo,
  mediaBreakpointMixin,
  MenuPopper,
  spaceMixin,
  Table,
  Tag,
  token,
} from '@zillow/constellation';

import Bone from '../../Bone';
import MediumIllustrationHouse2x from '../../../images/MediumIllustrationHouse@2x.png';

// vars
export const PROPERTY_ROW_WIDTHS = {
  FIRST_CELL: '36%',
  MIDDLE_CELL: '18%',
  LAST_CELL: '10%',
} as const;

const THEME_VALIDATION = 'props => props.theme && props.theme.ns';

const getImageDimensions = (isZram: boolean) => ({
  WIDTH: isZram ? 136 : 100, // max-width of image
  HEIGHT: isZram ? 88 : 60, // max-height of image
});
// TODO: this logic was duplicated in PropertyRow.tsx;
// we should refactor this to use a single source of truth as we phase out the remaining styled components
// may need to create a styles.ts for this when we migrate the more complex styles from this file
const getCellDimensions = (isZram = true) => ({
  WIDTH: 200, // max-width of cell
  HEIGHT: isZram ? 96 : 63, // min-height of cell
});

const HIDDEN_TEXT = `
    /* bypass the need for text to properly align */
    &:before {
        content: "hidden";
        display: flex;
        height: 0px;
        font-size: 0px;
    }
`;

export const StyledAnchor = styled(Anchor)`
  /* need value to be contents to display table content correctly */
  display: contents;
  text-decoration: none;
`;

export const StyledPropertyPhotoContainer = styled.div<{ isZram: boolean }>`
  display: flex;
  max-width: ${(props) => getImageDimensions(props.isZram).WIDTH + 32}px;
  min-height: ${(props) => getImageDimensions(props.isZram).HEIGHT}px;
  align-items: center;
  justify-content: center;
  margin-right: ${(props) => (props.isZram ? '0' : spaceMixin('md'))};

  ${HIDDEN_TEXT}
`;

export const StyledPropertyPhoto = styled.div<{ primaryPhotoUrl: string; isZram: boolean }>`
  width: ${(props) => getImageDimensions(props.isZram).WIDTH}px;
  height: ${(props) => getImageDimensions(props.isZram).HEIGHT}px;
  margin: 0 auto;
  border-radius: ${(props) => (props.isZram ? '0' : spaceMixin('xs'))};

  background-repeat: no-repeat;
  background-position: center center;
  background-size: ${(props) => (props.primaryPhotoUrl ? 'cover' : '100px')};
  background-image: url(${(props) => (props.primaryPhotoUrl ? props.primaryPhotoUrl : MediumIllustrationHouse2x)});

  ${(props) =>
    THEME_VALIDATION &&
    css`
      background-color: ${props.primaryPhotoUrl ? '#888' : token('colors.blue200')};
    `}
`;

export const StyledFlexContainer = styled(Flex)<{ isZram?: boolean }>`
  display: flex;
  align-items: ${(props) => (props.alignItems ? props.alignItems : 'center')};
  min-height: ${({ isZram = true }) => getCellDimensions(isZram).HEIGHT}px;
  max-width: ${({ isZram = true }) =>
    isZram ? `${getCellDimensions(isZram).WIDTH}px` : css`calc(100% - ${spaceMixin(16)})`};

  ${HIDDEN_TEXT}
`;

export const StyledMenuPopperWrapper = styled.div<{ isZram: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: solid 1px transparent;

  &:hover {
    border: solid 1px ${(props) => token(props.isZram ? 'colors.brand' : 'colors.borderBlue')};
    background-color: white;
    border-radius: 3px;
  }
`;

export const StyledPropertyAddress = styled.div<{ isMultifamily?: boolean; marginTop?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  /* IE11 support */
  min-height: ${(props) => (props.isMultifamily ? spaceMixin(9) : spaceMixin(6))};

  ${(props) =>
    props.marginTop &&
    css`
      margin-top: ${spaceMixin('xs')};
    `}

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const StyledPropertyRow = styled(Table.Row)<{ isZram: boolean }>`
  cursor: pointer;
  vertical-align: ${(props) => (props.isZram ? 'top' : 'middle')};
`;

export const StyledBoneContainer = styled(Bone)<{ width?: string }>`
  width: ${(props) => (props.width ? props.width : '50px')};
`;
export const StyledTagWrapper = styled.div`
  width: 100%;
`;

export const StyledIconZillowLogo = styled(IconZillowLogo)`
  margin: -4px 2px 0 0;
`;

export const StyledNotificationIcon = styled(IconCircle)`
  margin-left: ${spaceMixin('xs')};
  width: ${spaceMixin('xs')};
  color: ${token('colors.orangeRed400')};
`;

export const StyledTableCell = styled(Table.Cell)<{ isZram?: boolean }>`
  ${({ isZram = true }) =>
    !isZram &&
    css`
      width: ${PROPERTY_ROW_WIDTHS.MIDDLE_CELL};
      height: 105px;

      ${StyledAnchor}:last-child &&& {
        border-bottom: none;
      }

      &&&:first-child {
        padding-left: ${spaceMixin('md')};
        width: ${PROPERTY_ROW_WIDTHS.FIRST_CELL};
      }

      &&&:last-child {
        padding-right: ${spaceMixin('md')};
        width: ${PROPERTY_ROW_WIDTHS.LAST_CELL};
      }
    `}

  && {
    @media ${mediaBreakpointMixin('md')} {
      padding: ${spaceMixin('sm')} 0 ${spaceMixin('sm')} ${spaceMixin('sm')};

      &:last-child {
        padding-right: ${spaceMixin('sm')};
      }
    }

    @media ${mediaBreakpointMixin('lg')} {
      padding-left: ${spaceMixin('xs')};

      &:last-child {
        padding-right: ${spaceMixin('xs')};
      }
    }

    @media ${mediaBreakpointMixin('xl')} {
      ${({ isZram = true }) =>
        isZram &&
        css`
          padding: ${spaceMixin('sm')};

          &:last-child {
            padding-right: ${spaceMixin('sm')};
          }
        `}
    }
  }

  ${(props) =>
    props.isMinWidthNeeded &&
    css`
      min-width: ${getCellDimensions().WIDTH}px;
    `}
`;

export const StyledPlaceholderDiv = styled.div`
  display: inline;

  ${StyledTableCell}:hover & {
    display: none;
  }
`;

export const StyledContentDiv = styled.div`
  display: none;

  ${StyledTableCell}:hover & {
    display: block;
  }
`;

export const StyledTag = styled(Tag)`
  display: flex;
  width: fit-content;
  text-wrap: wrap;
`;

export const StyledMenuPopper = styled(MenuPopper)<{ isZram: boolean }>`
  && {
    color: ${({ isZram }) => (isZram ? token('colors.brand') : token('colors.borderBlue'))};
  }
`;

export const StyledCellAnchor = styled(Anchor)`
  color: ${token('colors.textDark')} !important;
  text-decoration: none;

  ${StyledTableCell}:hover & {
    color: ${token('colors.blue500')} !important;
    text-decoration: underline;
  }
`;
